import { ampli } from '@/ampli';
import { TokenStats } from '@/pages/api/tokens/info';
import { useEffect, useState } from 'react';
import { useMedia } from 'react-use';

import { IssuedByCircleBadge } from '@/components/Badges/IssuedByCircleBadge';
import { TokenSharingModal } from '@/components/TokenProfile/TokenSharingModal';
import { ZoraImageModal } from '@/components/TokenProfile/ZoraImageModal';
import { ZoraTokenChainImage } from '@/components/TokenProfile/ZoraTokenChainImage';

import { getIssuerBadgeLabel } from '@/constants/issuers';

import { ZoraToken } from '@/hooks/useZora';

import { buttons } from '@/styles/buttons';
import { fontStyles } from '@/styles/typography';
import { breakpoints } from '@/styles/util';

import { TokenAvatar } from '@/ui/Avatars/TokenAvatar';
import { Badge } from '@/ui/Badge';
import { ShareIcon } from '@/ui/Icons/ShareIcon';
import { VerificationIcon } from '@/ui/Icons/VerificationIcon';

import { classNames } from '@/utils/classnames';
import { isZoraToken, isZoraCreatorCoin, ZoraType } from '@/utils/zora';

import {
  MatchaTooltipContent,
  MatchaTooltipPopover,
  MatchaTooltipTrigger,
  MatchaTooltip,
} from '../../../ui/MatchaTooltip';
// utils
import { TokenResult } from '../../../utils/0x/token-registry.types';
import { makeImageProxyUrl } from '../../../utils/search';
import { SwitchButton } from '../SwitchButton';
import { TokenProfileMarketStats } from '../TokenProfileMarketStats';
import { TokenProfileHeaderLoader } from './TokenProfileHeaderLoader';
import { TokenSecondaryInfo } from './TokenSecondaryInfo';
import { ZoraCreatorInfo } from './ZoraCreatorInfo';

const LONG_TOKEN_NAME_LENGTH = 15;
export const LONG_TOKEN_SYMBOL_LENGTH = 10;

interface TokenProfileHeaderProps {
  /** Represents the token (either sell or buy) currently selected by the user. This is the token the user is focusing on in the token page. */
  activeToken: TokenResult | undefined;
  /** The display name of the token which overrides the erc20 tokens `name` property */
  displayName?: string;
  /** Inactive token  */
  inactiveToken?: TokenResult;
  /** Whether the component is still fetching information. */
  isLoading: boolean;
  /** Callback when a token is selected */
  onTokenSelect?: (token: TokenResult) => void;
  /** Zora Token info */
  zora?: ZoraToken;
  /** Token stats */
  tokenStats?: TokenStats;
}

/**
 * Heading with information about the active token.
 * @param props - TokenProfileHeader props
 */
export const TokenProfileHeader = ({
  activeToken,
  displayName,
  inactiveToken,
  isLoading,
  onTokenSelect,
  zora,
  tokenStats,
}: TokenProfileHeaderProps) => {
  const [modalOpenTime, setModalOpenTime] = useState(0);
  const isLongTokenName = activeToken && activeToken.name.length > LONG_TOKEN_NAME_LENGTH;
  const isLongTokenSymbol = activeToken && activeToken.symbol.length > LONG_TOKEN_SYMBOL_LENGTH;
  const isActiveTokenNativeUsdc = activeToken?.issuer?.name === 'circle';
  const isDesktop = useMedia(breakpoints.desktop, false);
  const isMobile = useMedia(breakpoints.mobile, false);
  const isZora = !!activeToken && isZoraToken(activeToken);
  const zoraType: ZoraType =
    activeToken && isZora ? (isZoraCreatorCoin(activeToken) ? 'creator' : 'post') : undefined;

  const [isNameExpanded, setIsNameExpanded] = useState(false);
  const [shouldShowShareModal, setShouldShowShareModal] = useState(false);
  const [shouldShowZoraImageModal, setShouldShowZoraImageModal] = useState(false);

  // we have situations (e.g. switching tokens) in which the tokenprofile header gets replaced without
  // being properly dismounted.
  // we also want to revert the change if `isSmallDevice` changes from a falsy to a truthy state
  useEffect(() => {
    if ((!isLongTokenName && isNameExpanded) || (isNameExpanded && isDesktop)) {
      setIsNameExpanded(false);
    }
  }, [isLongTokenName, isNameExpanded, isDesktop]);

  const onShare = () => {
    setModalOpenTime(Date.now());
    setShouldShowShareModal(true);
    if (activeToken) {
      ampli.tokenShareClicked({
        token_address: activeToken.address,
        token_chain_id: activeToken?.chainId,
        token_chain_name: activeToken.chainName,
        token_symbol: activeToken.symbol,
      });
    }
  };

  // skeleton loader
  if (!activeToken) {
    return <TokenProfileHeaderLoader zoraType={zoraType} />;
  }

  const issuerBadgeLabel = getIssuerBadgeLabel(activeToken.issuer);

  // Zora post layout - vertical on mobile, horizontal on desktop
  if (zoraType === 'post') {
    return (
      <div className="flex flex-col tablet:flex-row gap-4 tablet:gap-6 items-center p-6">
        <div className="flex-shrink-0 w-full tablet:w-auto">
          <ZoraTokenChainImage
            chainId={activeToken.chainId}
            logoUrl={
              activeToken.logo
                ? makeImageProxyUrl(activeToken.chainId, activeToken.address, true)
                : ''
            }
            tokenName={activeToken.name}
            onClick={() => setShouldShowZoraImageModal(true)}
            className="w-full h-auto tablet:w-[180px] tablet:h-[180px] max-h-[300px] tablet:max-h-[180px] aspect-square"
          />
        </div>
        <div className="flex-1 min-w-0 w-full tablet:w-auto">
          <div className="flex justify-between items-start mb-4 gap-2">
            <div className="min-w-0 flex-1">
              <h1
                className={fontStyles({
                  variant: 'H4',
                  class: [
                    'leading-normal whitespace-nowrap overflow-hidden text-ellipsis h-max ml-1 tablet:text-750',
                    isNameExpanded && isLongTokenName
                      ? 'text-ellipsis-none whitespace-normal break-words'
                      : '',
                  ],
                })}
              >
                {displayName ?? activeToken.name}
              </h1>
              <ZoraCreatorInfo activeToken={activeToken} zora={zora} />
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {!isMobile && (
                <SwitchButton
                  activeToken={activeToken}
                  inactiveToken={inactiveToken}
                  onTokenSelect={onTokenSelect}
                  className="mr-1"
                />
              )}
              <div
                className={buttons({ shape: 'circle', color: 'gray', size: 'small' })}
                onClick={onShare}
              >
                <ShareIcon />
              </div>
            </div>
          </div>
          <TokenSecondaryInfo token={activeToken} zoraType={zoraType} stats={tokenStats} />
          <TokenProfileMarketStats isLoading={isLoading} tokenStats={tokenStats} />
        </div>
        <TokenSharingModal
          chainId={activeToken.chainId}
          address={activeToken.address}
          symbol={activeToken.symbol}
          openTime={modalOpenTime}
          isOpen={shouldShowShareModal}
          handleOpenChange={setShouldShowShareModal}
        />
        <ZoraImageModal
          isOpen={shouldShowZoraImageModal}
          onClose={() => setShouldShowZoraImageModal(false)}
          imageUrl={makeImageProxyUrl(activeToken.chainId, activeToken.address, true)}
          description={
            isZoraCreatorCoin(activeToken) ? zora?.profile?.bio : zora?.coin?.description
          }
        />
      </div>
    );
  }

  // Standard token layout - vertical with image on top
  return (
    <div
      className="flex flex-col p-3 pb-2 pt-4 tablet:p-5 tablet:pb-4 tablet:pt-6 bg-transparent z-10 h-fit"
      style={{ gridArea: 'profileHeader' }}
    >
      <div className="flex items-start flex-row justify-between w-full mb-2.5">
        <TokenAvatar
          address={activeToken.address}
          chainId={activeToken.chainId}
          size={40}
          className="ml-1 hidden! tablet:block!"
        />
        <TokenAvatar
          address={activeToken.address}
          chainId={activeToken.chainId}
          size={32}
          className="ml-1 tablet:hidden"
        />
        <div className="flex">
          {!isMobile && (
            <SwitchButton
              activeToken={activeToken}
              inactiveToken={inactiveToken}
              onTokenSelect={onTokenSelect}
              className="mr-1"
            />
          )}
          <div
            className={buttons({
              color: 'gray',
              shape: 'pill',
              size: 'small',
              class: ['px-2.5', 'hover:cursor-pointer'],
            })}
            onClick={onShare}
          >
            <ShareIcon />
          </div>
        </div>
      </div>
      <div
        data-expanded={(isLongTokenName || isLongTokenSymbol) && isNameExpanded}
        onClick={() => {
          if ((isLongTokenName || isLongTokenSymbol) && !isDesktop) {
            setIsNameExpanded(!isNameExpanded);
          }
        }}
        className={classNames(
          'grid justify-start gap-1 items-center mb-1 desktop:flex desktop:mb-1.5 desktop:data-[expanded=true]:grid-flow-col',
          isNameExpanded ? 'auto-rows-auto' : 'grid-flow-col',
        )}
      >
        <MatchaTooltip>
          <MatchaTooltipTrigger>
            <h1
              data-long={isLongTokenName}
              className={fontStyles({
                variant: 'H4',
                class: [
                  'whitespace-nowrap overflow-hidden leading-normal text-ellipsis h-max ml-1 tablet:text-750',
                  isNameExpanded && isLongTokenName
                    ? 'text-ellipsis-none whitespace-normal break-words'
                    : '',
                ],
              })}
            >
              {displayName ?? activeToken.name}
            </h1>
          </MatchaTooltipTrigger>
          <MatchaTooltipContent>{activeToken.name}</MatchaTooltipContent>
          {isLongTokenName && (
            <MatchaTooltipPopover sideOffset={0} style={{ maxWidth: 'fit-content' }} />
          )}
        </MatchaTooltip>
        {!isZora && (
          <div data-expanded={(isLongTokenName || isLongTokenSymbol) && isNameExpanded}>
            <MatchaTooltip>
              <MatchaTooltipTrigger>
                <h2
                  data-long={isLongTokenSymbol}
                  className={fontStyles({
                    variant: 'BODY_LG',
                    class:
                      'h-max overflow-ellipsis overflow-hidden whitespace-nowrap text-500 mr-2 text-token-gray-700 font-normal max-w-[140px] leading-9 tablet:max-w-[150px] desktop:text-600 desktop:ml-2 desktop:max-w-[180px] data-[expanded=true]:data-[long=true]:overflow-visible data-[expanded=true]:data-[long=true]:whitespace-normal data-[expanded=true]:data-[long=true]:break-words data-[expanded=true]:data-[long=true]:max-w-none data-[expanded=true]:data-[long=true]:ml-1',
                  })}
                >
                  {activeToken.symbol}
                </h2>
              </MatchaTooltipTrigger>
              <MatchaTooltipContent>{activeToken.symbol}</MatchaTooltipContent>
              {isLongTokenSymbol && (
                <MatchaTooltipPopover sideOffset={0} style={{ maxWidth: 'fit-content' }} />
              )}
            </MatchaTooltip>
          </div>
        )}
        {isActiveTokenNativeUsdc && <IssuedByCircleBadge className="mt-1" />}
        {isZora && (
          <Badge variant="neutralOutline" aria-label="Creator on Zora" className="mt-1 ml-3">
            Creator on Zora
          </Badge>
        )}
        {issuerBadgeLabel && (
          <div className="shrink-0">
            <Badge variant="neutralOutline" aria-label={issuerBadgeLabel}>
              <span className="flex items-center gap-0.5">
                <VerificationIcon className="size-2.5 text-token-gray-700 shrink-0" />
                {issuerBadgeLabel}
              </span>
            </Badge>
          </div>
        )}
      </div>
      <TokenSecondaryInfo token={activeToken} stats={tokenStats} zoraType={zoraType} />
      <TokenProfileMarketStats isLoading={isLoading} tokenStats={tokenStats} />
      <TokenSharingModal
        chainId={activeToken.chainId}
        address={activeToken.address}
        symbol={activeToken.symbol}
        openTime={modalOpenTime}
        isOpen={shouldShowShareModal}
        handleOpenChange={setShouldShowShareModal}
      />
    </div>
  );
};
